import { createPortal } from "preact/compat";
import { useEffect, useState } from "preact/hooks";
import { ANDROID_URL, detectPlatform, dismiss, IOS_URL, isDismissed, type StorePlatform } from "./appBanner";
import { AppleIcon, PlayStoreIcon } from "./icons";

const SHOW_DELAY_MS = 1000;
const SLIDE_MS = 300;

type BannerProps = {
  platform: StorePlatform;
  visible: boolean;
  onDismiss: () => void;
};

const Banner = ({ platform, visible, onDismiss }: BannerProps) => {
  const storeUrl = platform === "ios" ? IOS_URL : ANDROID_URL;
  const storeName = platform === "ios" ? "App Store" : "Google Play";
  const StoreIcon = platform === "ios" ? AppleIcon : PlayStoreIcon;

  return (
    <div class={`fixed bottom-0 left-0 right-0 z-[6000] transition-transform duration-300 ease-out ${visible ? "translate-y-0" : "translate-y-full"}`}>
      <div class="bg-white dark:bg-neutral-800 rounded-t-2xl shadow-[0_-4px_20px_rgba(0,0,0,0.15)] dark:shadow-[0_-4px_20px_rgba(0,0,0,0.4)] border-t border-gray-200 dark:border-neutral-700 px-5 py-5 max-w-lg mx-auto">
        <div class="flex flex-col items-center gap-3">
          <div class="flex items-center gap-3">
            <img src="/static/logo/nottldr_logo_black_128.jpg" alt="!tldr" class="w-12 h-12 rounded-xl flex-shrink-0" />
            <p class="text-base font-semibold text-gray-900 dark:text-gray-100">Get the app on</p>
          </div>
          <a
            href={storeUrl}
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center justify-center gap-2 w-48 px-5 py-3 text-base font-semibold bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-full hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
            data-test-click={`app-banner-${platform}`}
          >
            <StoreIcon size={20} />
            {storeName}
          </a>
          <button
            type="button"
            onClick={onDismiss}
            class="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
            data-test-click="app-banner-dismiss"
          >
            Continue on web
          </button>
        </div>
      </div>
    </div>
  );
};

/** Offered on a phone that can install the app, a second after the page is up, until dismissed (for a week). */
export const AppDownloadBanner = () => {
  const [platform, setPlatform] = useState<StorePlatform | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const detected = detectPlatform();
    if (!detected || isDismissed()) {
      return;
    }
    setPlatform(detected);
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    dismiss();
    setTimeout(() => setPlatform(null), SLIDE_MS);
  };

  if (!platform) {
    return null;
  }
  return createPortal(<Banner platform={platform} visible={visible} onDismiss={handleDismiss} />, document.body);
};
